import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuth } from './useAuth'
import {
  getLearningContentState,
  getLearningContentStates,
  learningContentKey,
  setLearningContentState,
  upsertLearningContentState,
  type LearningContentLocator,
  type LearningContentStateInput
} from '@/lib/learning-state'

export function useLearningStates(locators: LearningContentLocator[]) {
  const { user } = useAuth()
  const stableLocators = useMemo(
    () =>
      locators
        .filter((locator) => locator.contentId)
        .sort((a, b) =>
          learningContentKey(a).localeCompare(learningContentKey(b))
        ),
    [locators]
  )

  return useQuery({
    queryKey: [
      'learning-content-states',
      user?.id,
      stableLocators.map(learningContentKey).join('|')
    ],
    queryFn: async () => {
      if (!user?.id || !stableLocators.length) return new Map()
      return getLearningContentStates(stableLocators)
    },
    enabled: Boolean(user?.id && stableLocators.length),
    staleTime: 1000 * 60 * 5
  })
}

export function useLearningState(locator: LearningContentLocator) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['learning-content-state', user?.id, learningContentKey(locator)],
    queryFn: async () => {
      if (!user?.id || !locator.contentId) return null
      return getLearningContentState(locator)
    },
    enabled: Boolean(user?.id && locator.contentId),
    staleTime: 1000 * 60 * 5
  })
}

export function useUpsertLearningState() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      input: Omit<LearningContentStateInput, 'userId'> & { userId?: string }
    ) => {
      const userId = input.userId ?? user?.id
      if (!userId) throw new Error('User not logged in')
      return upsertLearningContentState({ ...input, userId })
    },
    onSuccess: (state) => {
      queryClient.invalidateQueries({
        queryKey: ['learning-content-states', state.user_id]
      })
      queryClient.invalidateQueries({
        queryKey: [
          'learning-content-state',
          state.user_id,
          learningContentKey({
            contentType: state.content_type,
            contentId: state.content_id
          })
        ]
      })
      queryClient.invalidateQueries({
        queryKey: ['path-story-readiness', state.user_id]
      })
    }
  })
}

export function useSetLearningState() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      input: Omit<LearningContentStateInput, 'userId'> & { userId?: string }
    ) => {
      const userId = input.userId ?? user?.id
      if (!userId) throw new Error('User not logged in')
      return setLearningContentState({ ...input, userId })
    },
    onSuccess: (state) => {
      queryClient.invalidateQueries({
        queryKey: ['learning-content-states', state.user_id]
      })
      queryClient.invalidateQueries({
        queryKey: [
          'learning-content-state',
          state.user_id,
          learningContentKey({
            contentType: state.content_type,
            contentId: state.content_id
          })
        ]
      })
      queryClient.invalidateQueries({
        queryKey: ['path-story-readiness', state.user_id]
      })
      queryClient.invalidateQueries({ queryKey: ['story-recommendations'] })
    }
  })
}
